import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Repeat, Loader2, Check, FileSignature, ChevronDown, ChevronUp, X } from "lucide-react";

const chf = (v, c = "CHF") => `${c} ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const INTERVAL = { weekly: "wöchentlich", monthly: "monatlich", quarterly: "pro Quartal", yearly: "jährlich" };
const fmtMonth = (d) => (d ? new Date(d).toLocaleDateString("de-CH", { month: "short", year: "numeric" }) : "–");

function SeriesRow({ s, accounts, companyId, onBooked }) {
  const [account, setAccount] = useState((s.suggested_account || "").split(" ")[0] || "");
  const [createContract, setCreateContract] = useState(!s.contract_id);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const book = async () => {
    if (!account) { toast.error("Bitte ein Konto wählen."); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/subscription-series/book`, { item_ids: s.item_ids, account_code: account, create_contract: createContract });
      toast.success(`${r.data.count}× ${s.supplier} auf Konto ${r.data.account_code} gebucht${r.data.contract?.created ? ` · Vertrag «${r.data.contract.title}» angelegt` : ""}${r.data.failed?.length ? ` · ${r.data.failed.length} fehlgeschlagen` : ""}`);
      onBooked();
    } catch (e) { toast.error(e.response?.data?.detail || "Buchen fehlgeschlagen."); }
    finally { setBusy(false); }
  };
  const dismiss = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/inbox/subscription-series/dismiss`, { item_ids: s.item_ids });
      toast.success(`${s.count} Belege von ${s.supplier} erscheinen wieder einzeln in der Liste`);
      onBooked();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-violet-200 bg-white p-3" data-testid={`subscription-series-${s.key.replace(/[^a-z0-9]/gi, "-")}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900" data-testid="series-title">{s.count}× {s.supplier} · {chf(s.amount, s.currency)}</span>
            <Badge className="bg-violet-100 text-violet-800 border border-violet-200 text-[10px]">{INTERVAL[s.interval] || s.interval}</Badge>
            {s.contract_id && <Badge variant="outline" className="text-[10px] gap-1"><FileSignature className="h-3 w-3" />Vertrag: {s.contract_title}</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{fmtMonth(s.first_date)} – {fmtMonth(s.last_date)} · eigene Rechnungsnummern, gleicher Betrag → vermutlich wiederkehrend. «Alle buchen» ohne Haken = einzeln buchen, kein Vertrag</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={account} onChange={(e) => setAccount(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs max-w-[240px]" data-testid="series-account-select">
            <option value="">Konto wählen …</option>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
          </select>
          {!s.contract_id && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={createContract} onChange={(e) => setCreateContract(e.target.checked)} className="h-3.5 w-3.5 accent-violet-600" data-testid="series-create-contract" />Als Vertrag/Abo anlegen
            </label>
          )}
          <Button size="sm" onClick={book} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="series-book-all">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Alle {s.count} buchen
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss} disabled={busy} className="h-8 px-2 text-xs text-slate-500 hover:text-slate-900" title="Serie auflösen – Belege einzeln in der Liste anzeigen" data-testid="series-dismiss">
            <X className="h-3.5 w-3.5 mr-1" />Kein Abo
          </Button>
          <button type="button" onClick={() => setOpen((v) => !v)} className="text-slate-400 hover:text-slate-700" data-testid="series-toggle-items">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        </div>
      </div>
      {open && (
        <ul className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3 text-[11px] text-slate-600" data-testid="series-items">
          {s.items.map((i) => <li key={i.id} className="truncate">{i.date ? new Date(i.date).toLocaleDateString("de-CH") : "–"} · Nr. {i.invoice_number || "–"} · {i.file_name}</li>)}
        </ul>
      )}
    </div>
  );
}

export function SubscriptionSeriesCard({ companyId, refreshKey, onBooked }) {
  const [series, setSeries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/inbox/subscription-series`).then((r) => setSeries(r.data.series || [])).catch(() => setSeries([]));
  }, [companyId, refreshKey, bump]);
  const afterAction = (s) => {
    setSeries((prev) => prev.filter((x) => x.key !== s.key));
    setBump((b) => b + 1);
    onBooked?.();
  };
  useEffect(() => {
    if (!companyId || series.length === 0 || accounts.length) return;
    apiClient.get(`/companies/${companyId}/accounts`).then((r) => setAccounts((r.data || []).filter((a) => a.type === "expense"))).catch(() => {});
  }, [companyId, series.length, accounts.length]);
  if (series.length === 0) return null;
  return (
    <Card className="p-4 border-violet-200 bg-violet-50/50" data-testid="subscription-series-card">
      <div className="flex items-center gap-2 mb-3">
        <Repeat className="h-4 w-4 text-violet-700" />
        <h3 className="text-sm font-semibold text-violet-900">Wiederkehrende Abos erkannt</h3>
        <span className="text-xs text-violet-700">{series.length} Serie{series.length === 1 ? "" : "n"} – ein Konto wählen und alle Belege auf einmal buchen</span>
      </div>
      <div className="space-y-2">{series.map((s) => <SeriesRow key={s.key} s={s} accounts={accounts} companyId={companyId} onBooked={() => afterAction(s)} />)}</div>
    </Card>
  );
}
